import { Injectable } from '@angular/core';
import { AppConfigService, Page } from '@markant-platform/shared/data-access';
import {
  FileMetaInformation,
  FileMetaInformationParameters,
  FileUpload,
} from '@markant-platform/shared/organization-document/model';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OrganizationDocumentService {
  apiUrl: string = AppConfigService.appConfiguration.apiUrl['organizationDocumentService'];

  constructor(private readonly http: HttpClient) {}

  getDocumentsForOrganization(
    organizationId: string,
    cartridgeVariantId: string | undefined,
    { index, size, search, filter, sort = undefined }: FileMetaInformationParameters
  ): Observable<Page<FileMetaInformation>> {
    const queryParameters = this.buildQueryParameters({ index, size, search, filter, sort });
    const cartridgeParameter = cartridgeVariantId ? `&cartridgeVariantId=${cartridgeVariantId}` : '';

    return this.http.get<Page<FileMetaInformation>>(
      `${this.apiUrl}/organization/${organizationId}${queryParameters}${cartridgeParameter}`
    );
  }

  getDocumentsCountOfCurrentOrganization(): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/organization/current/count`);
  }

  getDocumentsForOrganizationAndCartridgeVariantId(
    organizationId: string,
    cartridgeVariantId: string,
    { index, size, search, filter, sort = undefined }: FileMetaInformationParameters
  ): Observable<Page<FileMetaInformation>> {
    const queryParameters = this.buildQueryParameters({ index, size, search, filter, sort });

    return this.http.get<Page<FileMetaInformation>>(
      `${this.apiUrl}/organization/${organizationId}/${cartridgeVariantId}${queryParameters}`
    );
  }

  getDocumentsCountForOrganization(organizationId: string, cartridgeVariantId?: string): Observable<number> {
    const cartridgeVariantIdParam = cartridgeVariantId ? `/${cartridgeVariantId}` : '';

    return this.http.get<number>(`${this.apiUrl}/organization/${organizationId}${cartridgeVariantIdParam}/count`);
  }

  downloadDocument(organizationId: string | undefined, docId: string, cartridgeVariantId?: string): Observable<never> {
    const httpOptions = {
      responseType: 'blob' as 'json',
    };

    if (!organizationId) {
      return this.http.get<never>(`${this.apiUrl}/file/${docId}`, httpOptions);
    }

    const downloadUrl = cartridgeVariantId
      ? `${this.apiUrl}/organization/${organizationId}/${cartridgeVariantId}/file/${docId}`
      : `${this.apiUrl}/organization/${organizationId}/file/${docId}`;

    return this.http.get<never>(downloadUrl, httpOptions);
  }

  /**
   * Upload documents for an organization. It is optional, to upload the documents in the context of a cartridge-variant.
   *
   * @param organizationId
   * @param documents
   * @param cartridgeVariantId - optional
   */
  uploadDocuments(
    organizationId: string,
    documents: FileUpload[],
    cartridgeVariantId?: string
  ): Observable<FileMetaInformation[]> {
    const requests: Observable<FileMetaInformation>[] = documents.map(d => {
      const payload = new FormData();
      payload.set('file', d.file, d.file.name);
      payload.set('format', d.format);

      const uploadUrl = cartridgeVariantId
        ? `${this.apiUrl}/organization/${organizationId}/${cartridgeVariantId}/file`
        : `${this.apiUrl}/organization/${organizationId}/file`;

      return this.http.post<FileMetaInformation>(uploadUrl, payload);
    });

    return forkJoin(requests);
  }

  private buildQueryParameters({ index, size, search, filter, sort }: FileMetaInformationParameters): string {
    const searchParam: string = search ? `&search=${search}` : '';
    const filterParam: string = filter ? `&${filter}` : '';
    const sortParams: string = sort ? `&sortField=${sort.sortField}&sortOrder=${sort.sortOrder}` : '';

    return `?index=${index}&size=${size}${searchParam}${filterParam}${sortParams}`;
  }
}
